import { useState, useEffect, useRef, useCallback } from 'react';
import { BarChart3, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useReviews } from '@/hooks/useReviews';
import { weekKeys, type Review } from '@trackmind/core';
import { cn, toLocalDate } from '@/lib/utils';
import { useToday } from '@/hooks/useToday';

interface WeeklyReviewWidgetProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export function WeeklyReviewWidget({ isCollapsed, onToggleCollapse }: WeeklyReviewWidgetProps) {
  const { getWeekly, error: reviewError } = useReviews();
  const todayKey = useToday();
  const [review, setReview] = useState<Review | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const hasLoadedRef = useRef(false);
  const getWeeklyRef = useRef(getWeekly);

  // Keep ref updated without triggering re-renders
  useEffect(() => {
    getWeeklyRef.current = getWeekly;
  }, [getWeekly]);

  const loadReview = useCallback(async () => {
    setLoading(true);
    try {
      const weeklyReview = await getWeeklyRef.current();
      setReview(weeklyReview);
    } catch (error) {
      console.error('Failed to load weekly review:', error);
      // Leave `review` null so the render below shows the failure rather than
      // a zeroed card. A 0/100 that actually means "this crashed" is what made
      // the same report come back four times.
      setReview(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load when widget becomes visible
  useEffect(() => {
    if (isCollapsed) return;
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadReview();
    }
  }, [isCollapsed, loadReview]);

  // Periodically refresh to pick up task/habit completions
  useEffect(() => {
    if (isCollapsed) return;

    const interval = setInterval(() => {
      loadReview();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [isCollapsed, loadReview]);

  // ReviewContent stores `tasks_completed` (a TaskSummary) and
  // `habits_summary`. This previously read `task_summary` / `habit_summary`,
  // which don't exist — so the widget silently rendered 0/0 and 0%.
  const taskSummary = review?.content.tasks_completed;
  const tasksDone = taskSummary?.completed_count ?? 0;
  // TaskSummary has no explicit total, so "everything raised this period" is
  // what completed plus what went stale.
  const tasksTotal = tasksDone + (taskSummary?.stale_count ?? 0);
  const taskCompletion = tasksTotal > 0 ? Math.round((tasksDone / tasksTotal) * 100) : 0;

  const habitsSummary = review?.content.habits_summary ?? [];
  // completion_rate is ALREADY a percentage (0–100). Multiplying by 100 again
  // was harmless only while adherence was stuck at zero; with real instance
  // data behind it that rendered "5,000%" and a pegged bar.
  const habitCompletion = habitsSummary.length > 0
    ? Math.round(
        habitsSummary.reduce((sum, h) => sum + h.completion_rate, 0) / habitsSummary.length,
      )
    : 0;

  const productivityScore = review?.content.productivity_score ?? 0;

  // Determine trend icon
  const getTrendIcon = () => {
    if (!review?.content.productivity_score) return <Minus className="h-3 w-3 text-muted-foreground" />;
    if (productivityScore >= 70) return <TrendingUp className="h-3 w-3 text-green-500" />;
    if (productivityScore <= 40) return <TrendingDown className="h-3 w-3 text-red-500" />;
    return <Minus className="h-3 w-3 text-yellow-500" />;
  };

  // Get week date range
  // The days the review covers, or this Monday-first week of the user's
  // today (weekKeys) before it has loaded — not a second week calculation.
  const getWeekRange = () => {
    const week = weekKeys(todayKey());
    const start = review?.period_start ?? week[0];
    const end = review?.period_end ?? week[6];
    const formatDate = (key: string) => {
      const d = toLocalDate(key);
      return `${d.getMonth() + 1}/${d.getDate()}`;
    };
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  return (
    <Card className="bg-card/50">
      <CardHeader className="py-2 px-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Weekly Review
          </CardTitle>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onToggleCollapse}>
            {isCollapsed ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
          </Button>
        </div>
      </CardHeader>

      {!isCollapsed && (
        <CardContent className="py-2 px-3">
          {loading ? (
            <p className="text-xs text-muted-foreground text-center py-2">Loading...</p>
          ) : !review ? (
            /* A failure and an empty week are NOT the same thing, and drawing
               both as zeros is precisely why this widget was reported broken
               four separate times. Say which one it is. */
            <div className="py-2 text-center">
              <p className="text-xs text-muted-foreground">
                {reviewError ? "Couldn't build this review" : 'No review data'}
              </p>
              {reviewError && (
                <p className="mt-1 text-[10px] text-red-500/80">
                  Something in this week's data broke it — please report this.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {/* Collapsed summary view (default) */}
              {!expanded && (
                <button
                  onClick={() => setExpanded(true)}
                  className="w-full text-left space-y-2"
                >
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{getWeekRange()}</span>
                    <div className="flex items-center gap-1">
                      {getTrendIcon()}
                      <span className={cn(
                        "font-medium",
                        productivityScore >= 70 ? "text-green-500" :
                          productivityScore <= 40 ? "text-red-500" : "text-yellow-500"
                      )}>
                        {productivityScore}/100
                      </span>
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Click to expand
                  </p>
                </button>
              )}

              {/* Expanded view */}
              {expanded && (
                <>
                  <div className="text-xs text-muted-foreground mb-2">{getWeekRange()}</div>

                  {/* Task completion */}
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span>Tasks</span>
                      <span className="text-muted-foreground">
                        {tasksDone}/{tasksTotal}
                      </span>
                    </div>
                    <Progress value={taskCompletion} className="h-1.5" />
                  </div>

                  {/* Habit compliance */}
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span>Habits</span>
                      <span className="text-muted-foreground">{habitCompletion}%</span>
                    </div>
                    <Progress value={habitCompletion} className="h-1.5" />
                  </div>

                  {/* Productivity score */}
                  <div className="flex items-center justify-between text-xs pt-1 border-t border-border/50">
                    <span className="flex items-center gap-1">
                      Score
                      {getTrendIcon()}
                    </span>
                    <span className={cn(
                      "font-medium",
                      productivityScore >= 70 ? "text-green-500" :
                        productivityScore <= 40 ? "text-red-500" : "text-yellow-500"
                    )}>
                      {productivityScore}/100
                    </span>
                  </div>

                  {/* Highlights */}
                  {review.content.highlights && review.content.highlights.length > 0 && (
                    <div className="pt-1">
                      <p className="text-[10px] text-muted-foreground mb-1">Highlights:</p>
                      <p className="text-xs truncate">{review.content.highlights[0]}</p>
                    </div>
                  )}

                  {/* Notes */}
                  {review.notes && (
                    <div className="pt-1">
                      <p className="text-[10px] text-muted-foreground mb-1">Note:</p>
                      <p className="text-xs line-clamp-2">{review.notes}</p>
                    </div>
                  )}

                  <button
                    onClick={() => setExpanded(false)}
                    className="text-xs text-muted-foreground hover:text-foreground w-full text-left"
                  >
                    ▾ Collapse
                  </button>
                </>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
